/**
 * Inference Context Menu Service
 *
 * Adds right-click context menu commands to Image and Video objects
 * in the Scene Explorer for ML inference plugins.
 *
 * Core inference logic lives in inference/; this file adds
 * toast UI and Babylon Inspector menu registration.
 *
 * Adding a new inference service only requires adding a plugin file in inference/.
 */

import {
    SceneExplorerServiceIdentity,
    type ServiceDefinition,
    type ISceneExplorerService,
} from '@babylonjs/inspector';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { TraceViewerElement } from 'trace-viewer';
import type { SceneObject } from 'trace-viewer/scene';
import { resolveSceneObject } from './export-context-menu-service';
import { showSpinnerToast, showToast } from './toast';
import type { PluginContextMenu } from './plugins/types';
import { getContextMenuEntries } from './plugins/registry';

const getViewer = (): TraceViewerElement | null =>
    document.querySelector('trace-viewer');

// ── Babylon Inspector Context Menu ─────────────────────────

export const InferenceContextMenuServiceDefinition: ServiceDefinition<[], [ISceneExplorerService]> = {
    friendlyName: 'Inference Context Menu',
    consumes: [SceneExplorerServiceIdentity],

    factory: (sceneExplorerService: ISceneExplorerService) => {
        const disposables = getContextMenuEntries().map((entry) => {
            const predicate = (entity: unknown): entity is TransformNode => {
                try {
                    if (!(entity instanceof TransformNode)) return false;
                    if (entity.isDisposed()) return false;
                    const so = resolveSceneObject(entity);
                    if (!so) return false;
                    return entry.predicate(so);
                } catch {
                    return false;
                }
            };

            const cmd = sceneExplorerService.addEntityCommand<TransformNode>({
                order: entry.order,
                predicate,
                getCommand: (entity) => ({
                    displayName: entry.label,
                    mode: 'contextMenu' as const,
                    type: 'action' as const,
                    execute: () => {
                        const so = resolveSceneObject(entity);
                        if (so) {
                            runFromContextMenu(entry, so).catch((err: unknown) =>
                                console.error(`[Inference] ${entry.label} failed:`, err)
                            );
                        }
                    },
                }),
            });

            return cmd;
        });

        return {
            dispose: () => {
                for (const d of disposables) d.dispose();
            },
        };
    },
};

// ── Toast-wrapped inference runner ──────────────────────────

/**
 * Run a context menu entry with a spinner toast.
 * Used by both the Scene Explorer context menu and the viewport context menu.
 */
export async function runFromContextMenu(
    entry: PluginContextMenu,
    sourceObject: SceneObject,
): Promise<void> {
    const viewer = getViewer();
    if (!viewer?.traceScene) return;

    if (!sourceObject.src) {
        showToast(`Object has no source`, 'error');
        return;
    }

    const service = entry.label.match(/\(([^)]+)\)/)?.[1] ?? entry.label;
    const dismiss = showSpinnerToast(`${service}: generating "${sourceObject.name}"…`);
    const onStatus = (status: string) => dismiss.update(`${service}: ${status}`);
    try {
        const result = await entry.run(sourceObject, viewer.traceScene, onStatus);
        const resultName = result && 'name' in result ? result.name : 'object';
        dismiss(`Created "${resultName}"`, 'success');
    } catch (err) {
        console.error(`[Inference] ${entry.label} failed:`, err);
        const msg = err instanceof Error ? err.message : String(err);
        dismiss(`${entry.label} failed: ${msg}`, 'error');
    }
}
